import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axiosClient from '../../api/axiosClient';
import toast from 'react-hot-toast';
import { getUserInitial } from '../../utils/userDisplay';
import ProfileAvatar from '../../components/shared/ProfileAvatar';
import ProfileImageUpload from '../../components/shared/ProfileImageUpload';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div>
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
      {subtitle && (
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
      )}
    </div>
  </div>
);

const Profile = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation('admin');
  const { user, shopId, fetchUserData } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    avatar_url: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        avatar_url: user.avatar_url || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axiosClient.put('/users/profile', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      });
      
      if (response.data.ok) {
        toast.success('Profile updated successfully');
        // Refresh user data in the context
        await fetchUserData();
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = () => {
    if (!user?.role) return 'User';
    return user.role.charAt(0).toUpperCase() + user.role.slice(1);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className={`text-3xl font-bold ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>
          {t('admin.profile_title')}
        </h1>
        <p className={`mt-2 ${
          isDark ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {t('admin.profile_subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Card */}
        <Card className="lg:col-span-1">
          <div className="p-6 text-center">
            <div className="mb-4 flex justify-center">
              <ProfileAvatar
                name={user?.name || getUserInitial(user)}
                avatarUrl={formData.avatar_url}
                size="xl"
                className="rounded-2xl"
                previewable
              />
            </div>
            <h3 className={`text-lg font-medium ${
              isDark ? 'text-white' : 'text-zinc-900'
            }`}>
              {user?.name || 'User'}
            </h3>
            <p className={`text-sm ${
              isDark ? 'text-zinc-400' : 'text-zinc-500'
            }`}>
              {getRoleDisplayName()}
            </p>
            <p className={`text-sm mt-1 ${
              isDark ? 'text-zinc-400' : 'text-zinc-500'
            }`}>
              {user?.email}
            </p>
          </div>
        </Card>

        {/* Profile Form */}
        <Card className="lg:col-span-2">
          <CardHeader
            title={t('admin.profile_account_info')}
            subtitle={t('admin.profile_update_details')}
          />

          <form onSubmit={handleSubmit} className="space-y-6 p-4 md:p-5">
            <div>
              <label className={`block text-sm font-medium mb-3 ${
                isDark ? 'text-zinc-300' : 'text-zinc-700'
              }`}>
                Profile Photo
              </label>
              <div className="flex justify-center rounded-2xl border border-white/10 bg-white/30 p-4 dark:bg-zinc-800/30">
                <ProfileImageUpload
                  name={formData.name || user?.name || 'User'}
                  userId={user?.id}
                  shopId={shopId}
                  currentAvatarUrl={formData.avatar_url}
                  onUploadSuccess={(newUrl) => {
                    setFormData((prev) => ({ ...prev, avatar_url: newUrl }));
                  }}
                  onRemoveSuccess={() => {
                    setFormData((prev) => ({ ...prev, avatar_url: '' }));
                  }}
                  size="xl"
                />
              </div>
            </div>

            <div>
              <label htmlFor="name" className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-zinc-300' : 'text-zinc-700'
              }`}>
                {t('admin.profile_full_name')}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-xl border transition-all duration-200 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 dark:placeholder-zinc-400 focus:border-[--accent]/40 focus:ring-2 focus:ring-[--accent]/20 backdrop-blur-sm"
                placeholder="Enter your full name"
                style={{ '--accent': ACCENT }}
              />
            </div>

            <div>
              <label htmlFor="email" className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-zinc-300' : 'text-zinc-700'
              }`}>
                {t('admin.profile_email_address')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-xl border transition-all duration-200 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 dark:placeholder-zinc-400 focus:border-[--accent]/40 focus:ring-2 focus:ring-[--accent]/20 backdrop-blur-sm"
                placeholder="Enter your email address"
                style={{ '--accent': ACCENT }}
              />
            </div>

            <div>
              <label htmlFor="phone" className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-zinc-300' : 'text-zinc-700'
              }`}>
                {t('admin.profile_phone_number')}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-xl border transition-all duration-200 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 dark:placeholder-zinc-400 focus:border-[--accent]/40 focus:ring-2 focus:ring-[--accent]/20 backdrop-blur-sm"
                placeholder="Enter your phone number"
                style={{ '--accent': ACCENT }}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className={`px-4 py-2 rounded-xl border font-medium transition-colors duration-200 ${
                  isDark 
                    ? 'border-white/10 text-zinc-300 bg-zinc-900/40 hover:bg-zinc-800/50' 
                    : 'border-white/30 text-zinc-700 bg-white/50 hover:bg-white/70'
                } backdrop-blur-sm`}
              >
                {t('admin.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg clickable-item"
                style={{
                  backgroundColor: ACCENT,
                  boxShadow: '0 8px 20px rgba(125,102,51,.25)'
                }}
                onMouseEnter={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = '#6b562b';
                    e.target.style.boxShadow = '0 10px 24px rgba(125,102,51,.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = ACCENT;
                  e.target.style.boxShadow = '0 8px 20px rgba(125,102,51,.25)';
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = '0 0 0 2px rgba(125,102,51,.3)';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = '0 2px 4px rgba(125,102,51,.2)';
                }}
              >
                {loading ? t('admin.saving') : t('admin.save_changes')}
              </button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
